import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { OCC_CART_ID_CURRENT, OCC_USER_ID_ANONYMOUS, RoutingService, StateUtils } from '@spartacus/core';
import { Observable } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  switchMapTo,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { UserIdService } from '../../../../core/auth/user-auth/facade/user-id.service';
import {
  Article,
  Cart,
  CartActionSourceData,
  CartType,
  CuttingRequest,
  OrderEntry,
  PalletFlagsRequest,
  ReelCuttingRequest,
  Unit,
} from '../../../../core/model';
import { AsyncReturnService, isTempCartId } from '../../../../shared';
import { ItemListType } from '../../../tracking/events/tracking-events';
import { ActiveCartFacade, CartFacade } from '../facades';
import { CartActions } from '../store/actions';
import { StateWithCart } from '../store/cart-state';
import { CartSelectors } from '../store/selectors';

@Injectable()
export class ActiveCartService implements ActiveCartFacade {
  constructor(
    private store: Store<StateWithCart>,
    private cartFacade: CartFacade,
    private userIdService: UserIdService,
    private routingService: RoutingService,
    private asyncReturnService: AsyncReturnService
  ) {}

  getActiveCart(cartType: CartType, isViewCart: boolean = false): Observable<Cart | undefined> {
    let viewedCart = false;
    return this.getActiveCartId(cartType).pipe(
      switchMap((cartId) =>
        this.cartFacade.getCartEntity(cartId).pipe(
          map((cartEntity) => ({
            cartId,
            cart: cartEntity.value,
            isStable: !cartEntity.loading && cartEntity.processesCount === 0,
            loaded: (cartEntity.error || cartEntity.success) && !cartEntity.loading,
          }))
        )
      ),
      // we want to emit empty carts even if those are not stable
      // we want to switch to empty cart so no one would use old cartId which can be already obsolete
      // so the resulting stream looks like this: old_cart -> undefined -> new_cart
      filter(({ isStable, cart }) => isStable || this.isEmpty(cart)),
      withLatestFrom(this.userIdService.getUserId()),
      // Responsible for loading cart when it's not loaded (eg. app initialization when we have only cart id)
      tap(([{ cartId, cart, loaded, isStable }, userId]) => {
        if (userId && userId !== OCC_USER_ID_ANONYMOUS && isStable && this.isEmpty(cart) && !loaded && !isTempCartId(cartId)) {
          this.load(cartType, cartId, userId);
        }
        if (isViewCart && loaded && cart && !viewedCart) {
          viewedCart = true;
          this.store.dispatch(new CartActions.ViewCartEntries({ cartId, cartType, userId, entries: cart.entries }));
        }
      }),
      map(([{ cart }]) => (cart ? cart : undefined)),
      distinctUntilChanged()
    );
  }

  getActiveCartId(cartType: CartType): Observable<string> {
    return this.store.pipe(
      select(CartSelectors.getActiveCartIdFactory(cartType)),
      filter((cartId) => cartId !== undefined),
      distinctUntilChanged()
    );
  }

  getEntries(cartType: CartType): Observable<OrderEntry[]> {
    return this.getActiveCartId(cartType).pipe(
      switchMap((cartId) => this.cartFacade.getEntries(cartId)),
      distinctUntilChanged()
    );
  }

  getEntryById(entryId: string, cartType: CartType): Observable<OrderEntry | undefined> {
    return this.getActiveCartId(cartType).pipe(
      switchMap((cartId) => this.cartFacade.getEntryById(cartId, entryId)),
      distinctUntilChanged()
    );
  }

  getLoading(cartType: CartType): Observable<boolean> {
    return this.getActiveCartId(cartType).pipe(
      switchMap((cartId) => this.cartFacade.getCartEntity(cartId)),
      map((cartEntity) => cartEntity.loading),
      distinctUntilChanged()
    );
  }

  isStable(cartType: CartType): Observable<boolean> {
    // Debounce is used here, to avoid flickering when we switch between different cart entities.
    // For example during `addEntry` method. We might try to load current cart, so `current` cart will be then active id.
    // After load fails we might create new cart so we switch to `temp-${uuid}` cart entity used when creating cart.
    // At the end we finally switch to cart `code` for cart id. Between those switches cart `isStable` function should not flicker.
    return this.getActiveCartId(cartType).pipe(
      switchMap((cartId) => this.cartFacade.isStable(cartId)),
      distinctUntilChanged()
    );
  }

  addEntry(
    articleCode: string,
    quantity: number,
    customerSelectedUnit: Unit,
    cartType: CartType = CartType.stock,
    orderCardCode?: string,
    customerMaterialNumber?: string,
    cartTemplate?: string,
    itemListType?: ItemListType
  ): Observable<boolean> {
    return this.asyncReturnService.subscribeAndReturn(
      this.requireLoadedCart(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        switchMap(([cartState, userId]) => {
          const eCommerceQuantity = !!customerSelectedUnit ? customerSelectedUnit.inEcommerceUnit * quantity : quantity;
          return this.cartFacade.addEntry(
            userId,
            cartType,
            cartState.value?.code,
            articleCode,
            eCommerceQuantity,
            customerSelectedUnit,
            orderCardCode,
            customerMaterialNumber,
            cartTemplate,
            itemListType
          );
        })
      )
    );
  }

  addA4SampleEntry(productCode: string, grammage: number, color: string, quantity: number): Observable<boolean> {
    return this.asyncReturnService.subscribeAndReturn(
      this.requireLoadedCart(CartType.sample).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        switchMap(([cartState, userId]) =>
          this.cartFacade.addA4SampleEntry(userId, cartState.value?.code, productCode, grammage, color, quantity)
        )
      )
    );
  }

  quickAddEntry(
    cartType: CartType = CartType.stock,
    articleCode: string,
    quantity: number,
    customerSelectedUnit: Unit,
    note?: string,
    customerMaterialNumber?: string,
    itemListType?: ItemListType
  ): Observable<boolean> {
    return this.asyncReturnService.subscribeAndReturn(
      this.requireLoadedCart(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        switchMap(([cartState, userId]) => {
          const eCommerceQuantity = !!customerSelectedUnit ? customerSelectedUnit.inEcommerceUnit * quantity : quantity;
          return this.cartFacade.quickAddEntry(
            userId,
            cartType,
            cartState.value?.code,
            articleCode,
            eCommerceQuantity,
            customerSelectedUnit,
            note,
            customerMaterialNumber,
            itemListType
          );
        })
      )
    );
  }

  addEntries(
    entries: OrderEntry[],
    cartType: CartType = CartType.stock,
    sourceData?: CartActionSourceData,
    itemListType?: ItemListType
  ): Observable<boolean> {
    return this.asyncReturnService.subscribeAndReturn(
      this.requireLoadedCart(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        switchMap(([cartState, userId]) =>
          this.cartFacade.addEntries(userId, cartType, cartState.value?.code, entries, sourceData, itemListType)
        )
      )
    );
  }

  removeEntry(entry: OrderEntry, cartType: CartType): Observable<boolean> {
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) => this.cartFacade.removeEntry(userId, cartType, cartId, entry))
      )
    );
  }

  removeEntryByNumber(entryNumber: number, cartType: CartType): Observable<boolean> {
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCart(cartType).pipe(
        map((cart) => cart.entries),
        take(1),
        map((entries) => entries?.find((entry) => entryNumber === entry.entryNumber)),
        switchMap((entry) => this.removeEntry(entry, cartType))
      )
    );
  }

  removeEntryById(entryId: string, cartType: CartType): Observable<boolean> {
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCart(cartType).pipe(
        map((cart) => cart.entries),
        take(1),
        map((entries) => entries?.find((entry) => entryId === entry.id)),
        switchMap((entry) => this.removeEntry(entry, cartType))
      )
    );
  }

  removeAllEntries(cartType: CartType): Observable<boolean> {
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) => this.cartFacade.removeAllEntries(userId, cartType, cartId))
      )
    );
  }

  removeEntries(entries: OrderEntry[], cartType: CartType): Observable<boolean> {
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) => this.cartFacade.removeEntries(userId, cartType, cartId, entries))
      )
    );
  }

  updateEntry(entry: OrderEntry, quantity: number, unit: Unit, cartType: CartType): Observable<boolean> {
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) => this.cartFacade.updateEntry(userId, cartType, cartId, entry, quantity, unit))
      )
    );
  }

  updateEntryNote(entry: OrderEntry, note: string, cartType: CartType): Observable<boolean> {
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) => this.cartFacade.updateEntryNote(userId, cartType, cartId, entry, note))
      )
    );
  }

  updateNameMarking(
    entry: OrderEntry,
    qualifier: string,
    ruleId: string,
    extendedIdentifier: string,
    values: string[],
    cartType: CartType
  ): Observable<boolean> {
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) =>
          this.cartFacade.updateNameMarking(userId, cartType, cartId, entry, qualifier, ruleId, extendedIdentifier, values)
        )
      )
    );
  }

  updateEntryStatisticsCode(entry: OrderEntry, statisticsCode: string, cartType: CartType): Observable<boolean> {
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) =>
          this.cartFacade.updateEntryStatisticsCode(userId, cartType, cartId, entry, statisticsCode)
        )
      )
    );
  }

  replaceEntry(entry: OrderEntry, replacementArticle: Article, cartType: CartType): Observable<boolean> {
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) =>
          this.cartFacade.replaceEntry(
            userId,
            cartType,
            cartId,
            entry,
            replacementArticle.code,
            replacementArticle.minimumOrderQuantity,
            replacementArticle.unit
          )
        )
      )
    );
  }

  addEntryFullPalletNote(entry: OrderEntry, cartType: CartType): Observable<boolean> {
    if (cartType !== CartType.stock) {
      throw new Error('Full pallet note is only possible for stock carts');
    }
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) => this.cartFacade.addEntryFullPalletNote(userId, cartType, cartId, entry))
      )
    );
  }

  removeEntryFullPalletNote(entry: OrderEntry, cartType: CartType): Observable<boolean> {
    if (cartType !== CartType.stock) {
      throw new Error('Full pallet note is only possible for stock carts');
    }
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) => this.cartFacade.removeEntryFullPalletNote(userId, cartType, cartId, entry))
      )
    );
  }

  addEntryCutting(entry: OrderEntry, cutting: CuttingRequest, cartType: CartType): Observable<boolean> {
    if (cartType !== CartType.stock) {
      throw new Error('Cutting is only possible for stock carts');
    }
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) => this.cartFacade.addEntryCutting(userId, cartType, cartId, entry, cutting))
      )
    );
  }

  removeEntryCutting(entry: OrderEntry, cartType: CartType): Observable<boolean> {
    if (cartType !== CartType.stock) {
      throw new Error('Cutting is only possible for stock carts');
    }
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) => this.cartFacade.removeEntryCutting(userId, cartType, cartId, entry))
      )
    );
  }

  addEntryReelCutting(entry: OrderEntry, reelCutting: ReelCuttingRequest, cartType: CartType): Observable<boolean> {
    if (cartType !== CartType.stock) {
      throw new Error('Reel cutting is only possible for stock carts');
    }
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) => this.cartFacade.addEntryReelCutting(userId, cartType, cartId, entry, reelCutting))
      )
    );
  }

  removeEntryReelCutting(entry: OrderEntry, cartType: CartType): Observable<boolean> {
    if (cartType !== CartType.stock) {
      throw new Error('Reel cutting is only possible for stock carts');
    }
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) => this.cartFacade.removeEntryReelCutting(userId, cartType, cartId, entry))
      )
    );
  }

  addEntryReaming(entry: OrderEntry, cartType: CartType): Observable<boolean> {
    if (cartType !== CartType.stock) {
      throw new Error('Reaming is only possible for stock carts');
    }
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) => this.cartFacade.addEntryReaming(userId, cartType, cartId, entry))
      )
    );
  }

  removeEntryReaming(entry: OrderEntry, cartType: CartType): Observable<boolean> {
    if (cartType !== CartType.stock) {
      throw new Error('Reaming is only possible for stock carts');
    }
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) => this.cartFacade.removeEntryReaming(userId, cartType, cartId, entry))
      )
    );
  }

  updateEntryPalletFlags(entry: OrderEntry, palletFlags: PalletFlagsRequest, cartType: CartType): Observable<boolean> {
    if (cartType !== CartType.stock) {
      throw new Error('Pallet flags are only possible for stock carts');
    }
    return this.asyncReturnService.subscribeAndReturn(
      this.getActiveCartId(cartType).pipe(
        withLatestFrom(this.userIdService.getUserId()),
        take(1),
        filter(([_cartId, userId]) => userId !== OCC_USER_ID_ANONYMOUS),
        switchMap(([cartId, userId]) => this.cartFacade.updateEntryPalletFlags(userId, cartType, cartId, entry, palletFlags))
      )
    );
  }

  getLastEntry(articleCode: string, quantity: number, unit: Unit, cartType: CartType): Observable<OrderEntry> {
    return this.getActiveCartId(cartType).pipe(
      switchMap((cartId) => this.cartFacade.getLastEntry(cartId, articleCode, quantity, unit)),
      distinctUntilChanged()
    );
  }

  isMaxOrderLinesExceeded(cartType: CartType): Observable<boolean> {
    return this.getActiveCartId(cartType).pipe(switchMap((cartId) => this.cartFacade.isMaxOrderLinesExceeded(cartId)));
  }

  reloadActiveCart(cartType: CartType): void {
    this.getActiveCartId(cartType)
      .pipe(take(1))
      .subscribe((cartId) => {
        this.cartFacade.reloadCart(cartId, cartType);
      });
  }

  private load(cartType: CartType, cartId: string, userId: string): void {
    this.cartFacade.loadCart({
      userId,
      cartType,
      cartId: cartId,
      extraData: {
        active: true,
      },
    });
  }

  private requireLoadedCart(cartType: CartType): Observable<StateUtils.ProcessesLoaderState<Cart>> {
    const userId$ = this.userIdService.getUserId().pipe(take(1), shareReplay({ bufferSize: 1, refCount: true }));
    return userId$.pipe(
      tap((userId) => {
        if (userId === OCC_USER_ID_ANONYMOUS) {
          this.routingService.go({ cxRoute: 'login' });
        }
      }),
      filter((userId) => userId !== OCC_USER_ID_ANONYMOUS),
      switchMapTo(this.getActiveCartId(cartType)),
      switchMap((cartId) => this.cartFacade.getCartEntity(cartId)),
      filter((cartState) => !cartState.loading),
      withLatestFrom(userId$),
      tap(([cartState, userId]) => {
        if (this.isEmpty(cartState.value) && !cartState.error) {
          // Load means create if the cart does not exist
          this.load(cartType, OCC_CART_ID_CURRENT, userId);
        }
      }),
      map(([cartState]) => cartState),
      filter((cartState) => !cartState.loading),
      filter((cartState) => cartState.success || Boolean(cartState.error)),
      filter((cartState) => !this.isEmpty(cartState.value)),
      take(1)
    );
  }

  private isEmpty(cart: Cart | undefined): boolean {
    return !cart;
  }
}
